import { Check, ChevronDown, Globe2, Search } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { cn } from '@/lib/cn'

const ELEMENT_ID = 'google_translate_element'
const SCRIPT_ID = 'google-translate-element-script'
const CALLBACK_NAME = 'crystalGoogleTranslateInit'
const LANGUAGE_STORAGE_KEY = 'crystal-language'

interface LanguageOption {
  code: string
  label: string
}

type GoogleTranslateConstructor = new (
  options: { pageLanguage: string; autoDisplay: boolean; layout: number },
  elementId: string,
) => unknown

declare global {
  interface Window {
    google?: {
      translate?: {
        TranslateElement?: GoogleTranslateConstructor & {
          InlineLayout: { HORIZONTAL: number }
        }
      }
    }
    crystalGoogleTranslateInit?: () => void
  }
}

function mountGoogleTranslate(): boolean {
  const host = document.getElementById(ELEMENT_ID)
  const TranslateElement = window.google?.translate?.TranslateElement
  if (!host || !TranslateElement) return false

  // StrictMode mounts effects twice in development. Google owns the subtree after the first
  // initialization, so constructing a second widget would duplicate the translation engine.
  if (host.childElementCount === 0) {
    new TranslateElement(
      {
        pageLanguage: 'en',
        autoDisplay: false,
        layout: TranslateElement.InlineLayout.HORIZONTAL,
      },
      ELEMENT_ID,
    )
  }
  return true
}

function googleLanguages(): LanguageOption[] {
  const combo = document.querySelector<HTMLSelectElement>('#google_translate_element .goog-te-combo')
  const translated = combo
    ? Array.from(combo.options)
        .filter((option) => option.value)
        .map((option) => ({ code: option.value, label: option.text.trim() }))
    : []

  return [{ code: 'en', label: 'English' }, ...translated.filter(({ code }) => code !== 'en')]
}

function activeLanguageCode(): string {
  try {
    const cookie = document.cookie
      .split('; ')
      .find((part) => part.startsWith('googtrans='))
      ?.split('=')[1]
    const fromCookie = cookie ? decodeURIComponent(cookie).split('/').filter(Boolean).at(-1) : null
    return fromCookie || window.localStorage.getItem(LANGUAGE_STORAGE_KEY) || 'en'
  } catch {
    return 'en'
  }
}

function clearGoogleLanguageCookie() {
  const expired = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/'
  document.cookie = expired
  document.cookie = `${expired}; domain=${window.location.hostname}`
  const parts = window.location.hostname.split('.')
  if (parts.length > 2) document.cookie = `${expired}; domain=.${parts.slice(-2).join('.')}`
}

function normalise(value: string) {
  return value.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
}

/**
 * Searchable CrystalDEX language selector backed by Google Website Translator.
 *
 * Google's own visible picker is a cross-origin iframe: it cannot inherit our theme and has
 * no search. Its select remains mounted off-screen as the translation engine while this
 * component provides the accessible, searchable interface. English is always the canonical
 * source language and the first option.
 */
export function GoogleTranslate() {
  const rootRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const [languages, setLanguages] = useState<LanguageOption[]>([{ code: 'en', label: 'English' }])
  const [language, setLanguage] = useState(activeLanguageCode)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    const host = document.getElementById(ELEMENT_ID)
    if (!host) return

    const refreshLanguages = () => {
      const next = googleLanguages()
      if (next.length > 1) setLanguages(next)
    }
    const observer = new MutationObserver(refreshLanguages)
    observer.observe(host, { childList: true, subtree: true })

    window[CALLBACK_NAME] = () => {
      const mounted = mountGoogleTranslate()
      setFailed(!mounted)
      if (mounted) window.setTimeout(refreshLanguages, 0)
    }

    if (mountGoogleTranslate()) {
      window.setTimeout(refreshLanguages, 0)
    } else {
      const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null
      if (existing) {
        existing.addEventListener('error', () => setFailed(true), { once: true })
      } else {
        const script = document.createElement('script')
        script.id = SCRIPT_ID
        script.async = true
        script.src = `https://translate.google.com/translate_a/element.js?cb=${CALLBACK_NAME}`
        script.addEventListener('error', () => setFailed(true), { once: true })
        document.head.appendChild(script)
      }
    }

    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!open) return
    searchRef.current?.focus()
    const closeOnOutside = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', closeOnOutside)
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('pointerdown', closeOnOutside)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [open])

  const selectedLabel = languages.find(({ code }) => code === language)?.label ?? 'English'
  const filteredLanguages = useMemo(() => {
    const needle = normalise(query.trim())
    return needle ? languages.filter(({ label }) => normalise(label).includes(needle)) : languages
  }, [languages, query])

  const chooseLanguage = (option: LanguageOption) => {
    setLanguage(option.code)
    setQuery('')
    setOpen(false)
    try { window.localStorage.setItem(LANGUAGE_STORAGE_KEY, option.code) } catch { /* optional */ }

    if (option.code === 'en') {
      clearGoogleLanguageCookie()
      window.location.reload()
      return
    }

    const combo = document.querySelector<HTMLSelectElement>('#google_translate_element .goog-te-combo')
    if (!combo) {
      setFailed(true)
      return
    }
    combo.value = option.code
    combo.dispatchEvent(new Event('change', { bubbles: true }))
  }

  return (
    <div ref={rootRef} className="notranslate relative min-w-0" translate="no">
      <button
        type="button"
        className={cn(
          'inline-flex h-10 max-w-[9.75rem] items-center gap-2 rounded-[12px] px-3 text-sm font-semibold text-ink max-sm:size-10 max-sm:justify-center max-sm:px-0',
          'transition-[box-shadow,background,color] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
          open
            ? 'bg-accent-wash text-accent [box-shadow:inset_0_0_0_1px_var(--color-accent-edge)]'
            : '[background:var(--btn-ghost-bg)] [box-shadow:var(--btn-ghost-shadow)] hover:[background:var(--btn-ghost-bg-hi)]',
        )}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`Language: ${selectedLabel}`}
        disabled={failed}
        onClick={() => setOpen((value) => !value)}
      >
        <Globe2 className="size-4 shrink-0" aria-hidden />
        <span className="min-w-0 truncate max-sm:hidden">{failed ? 'English' : selectedLabel}</span>
        <ChevronDown className={cn('size-3.5 shrink-0 transition-transform max-sm:hidden', open && 'rotate-180')} aria-hidden />
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Choose language"
          className="absolute right-0 top-12 z-50 w-[min(22rem,calc(100vw-1rem))] overflow-hidden rounded-[18px] bg-surface shadow-xl ring-1 ring-line-strong max-sm:fixed max-sm:inset-x-2 max-sm:top-14 max-sm:w-auto"
        >
          <div className="border-b border-line p-3">
            <p className="label mb-2 px-1">Page language</p>
            <label className="flex h-10 items-center gap-2 rounded-[11px] bg-elevated px-3 ring-1 ring-line focus-within:ring-2 focus-within:ring-accent">
              <Search className="size-4 shrink-0 text-muted" aria-hidden />
              <span className="sr-only">Search languages</span>
              <input
                ref={searchRef}
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search languages…"
                className="min-w-0 flex-1 bg-transparent text-sm text-ink outline-none placeholder:text-faint"
              />
            </label>
          </div>

          <div className="max-h-80 overflow-y-auto p-2" role="listbox" aria-label="Languages">
            {filteredLanguages.length ? filteredLanguages.map((option) => {
              const selected = option.code === language
              return (
                <button
                  key={option.code}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onClick={() => chooseLanguage(option)}
                  className={cn(
                    'flex w-full items-center justify-between rounded-[10px] px-3 py-2.5 text-left text-sm transition-colors',
                    selected ? 'bg-accent-wash font-semibold text-accent' : 'text-ink hover:bg-elevated',
                  )}
                >
                  <span>{option.label}</span>
                  {selected && <Check className="size-4" aria-hidden />}
                </button>
              )
            }) : (
              <p className="px-3 py-8 text-center text-sm text-muted">No languages found.</p>
            )}
          </div>

          <div className="flex items-center justify-between border-t border-line px-4 py-2.5 text-[11px] text-faint">
            <span>English is the primary language</span>
            <a href="https://translate.google.com/" target="_blank" rel="noreferrer" className="font-semibold text-accent hover:underline">
              Translation by Google
            </a>
          </div>
        </div>
      )}

      {/* Google supplies the translation engine and language catalogue. It stays mounted but
          off-screen so its unthemeable cross-origin menu can never cover the application. */}
      <div id={ELEMENT_ID} className="google-translate-engine" aria-hidden />
    </div>
  )
}
